/* RESPONSIBLE TEAM: team-ai-insights */
/* ToDo: Localize these strings when the strings are finalized */
/* eslint-disable @intercom/intercom/no-bare-strings */
import { type LogicalFilter, type Filter } from 'embercom/components/reporting/custom/filters';
import {
  and,
  not,
  FinInsightsSankeyConfig,
} from 'embercom/components/reporting/automation/ai-insights/fin-insights/sankey-config';
import { type Aggregation } from 'embercom/objects/reporting/unified/metrics/types';
import type IntlService from 'ember-intl/services/intl';
import type AppService from 'embercom/services/app-service';

const groupings = {
  queryType: {
    informational: FinInsightsSankeyConfig.filters.queryTypeInformational,
    personalized: FinInsightsSankeyConfig.filters.queryTypePersonalized,
    taskBased: FinInsightsSankeyConfig.filters.queryTypeActions,
    advanced: FinInsightsSankeyConfig.filters.queryTypeComplex,
    // TODO: we don't have an agreed definition for "nonSupport" yet,
  },
  handled: {
    resolvedByFin: FinInsightsSankeyConfig.filters.finOnlyResolved,
    routedToTeammate: FinInsightsSankeyConfig.filters.finAndTeammate,
    handledFullyByTeammate: and(
      not(FinInsightsSankeyConfig.filters.finAnswered),
      FinInsightsSankeyConfig.filters.teammateWasEverInvolved,
    ),
  },
  csat: {
    positive: FinInsightsSankeyConfig.filters.positiveCSAT,
    negative: FinInsightsSankeyConfig.filters.negativeCSAT,
    neutral: FinInsightsSankeyConfig.filters.neutralCSAT,
  },
};

export interface ChartSeriesDefinitionWithoutLabel {
  metric_id: string;
  aggregation?: Aggregation;
  tooltip?: string;
  filters?: LogicalFilter | Filter;
}

export interface ChartSeriesDefinition extends ChartSeriesDefinitionWithoutLabel {
  label: string;
}

export function insightsMetrics(intl: IntlService): Record<string, ChartSeriesDefinition> {
  return {
    volume: {
      metric_id: 'v1.new_conversations',
      aggregation: 'count',
      label: intl.t('components.reporting.automation.fin-insights.new-conversations'),
    },
    resolutionRate: {
      metric_id: 'fin.resolution_rate',
      label: intl.t('components.reporting.automation.fin-insights.resolution-rate'),
    },
    handlingTimeSum: {
      metric_id: 'v1.handling_time',
      aggregation: 'sum',
      label: intl.t('components.reporting.automation.fin-insights.handling-time-sum'),
    },
    handlingTimeMedian: {
      metric_id: 'v1.handling_time',
      aggregation: 'median',
      label: intl.t('components.reporting.automation.fin-insights.handling-time-median'),
    },
    aiCsatScore: {
      metric_id:
        'conversation.ai_generated_metrics.csat_score.first_user_conversation_part_created_at',
      label: intl.t('components.reporting.automation.fin-insights.ai-customer-experience-score'),
      tooltip: intl.t(
        'components.reporting.automation.fin-insights.ai-customer-experience-score-tooltip',
      ),
    },
    negativelyRatedAiCsat: {
      metric_id:
        'conversation.ai_generated_metrics.csat.count.first_user_conversation_part_created_at',
      label: intl.t(
        'components.reporting.automation.fin-insights.negatively-rated-ai-customer-experience-score',
      ),
      tooltip: intl.t(
        'components.reporting.automation.fin-insights.negatively-rated-ai-customer-experience-score-tooltip',
      ),
      // TODO: check that we want this
      filters: FinInsightsSankeyConfig.filters.negativeCSAT,
    },
    positivelyRatedAiCsat: {
      metric_id:
        'conversation.ai_generated_metrics.csat.positively_rated.count.first_user_conversation_part_created_at',
      label: intl.t(
        'components.reporting.automation.fin-insights.positively-rated-ai-customer-experience-score',
      ),
      tooltip: intl.t(
        'components.reporting.automation.fin-insights.positively-rated-ai-customer-experience-score-tooltip',
      ),
    },
    involvementRate: {
      metric_id: 'fin.participation_rate',
      label: intl.t('components.reporting.automation.fin-insights.involvement-rate'),
    },
    firstResponseTime: {
      metric_id: 'v2.first_response_time.bot_inbox_included',
      aggregation: 'median',
      label: intl.t('components.reporting.automation.fin-insights.first-response-time-median'),
    },
  } as const;
}

function jobDefinitions(intl: IntlService, appService: AppService) {
  let metrics = insightsMetrics(intl);

  return [
    {
      id: 'identifyFinContentGaps',
      label: intl.t('components.reporting.automation.fin-insights.jobs.content-gaps.label'),
      showSuggestions: true,
      backgroundClass: 'bg-vis-green-50',
      hoverBackgroundClass: 'hover:bg-vis-green-70',
      sankeyColour: 'vis-green-50',
      fontClass: 'text-default',
      table: {
        cta: 'components.reporting.automation.fin-insights.jobs.content-gaps.table-cta',
        filters: and(groupings.queryType.informational, groupings.handled.routedToTeammate),
        chartSeries: [
          metrics.volume,
          metrics.handlingTimeSum,
          metrics.handlingTimeMedian,
          metrics.aiCsatScore,
        ],
        highlightedMetricIndex: 1, // total handling time
      },
      sankey: {
        type: 'queryTypeToRes',
        highlightedLinks: 'infoToRoutedToTeammate',
      },
      topLine: {
        chartSeries: [metrics.volume],
        filters: and(groupings.queryType.informational, groupings.handled.routedToTeammate),
        label: intl.t(
          'components.reporting.automation.fin-insights.jobs.content-gaps.top-line.label',
        ),
        description: intl.t(
          'components.reporting.automation.fin-insights.jobs.content-gaps.top-line.description',
        ),
      },
    },
    {
      id: 'identifyFinDataGaps',
      label: intl.t('components.reporting.automation.fin-insights.jobs.data-gaps.label'),
      showSuggestions: false,
      backgroundClass: 'bg-vis-green-50',
      hoverBackgroundClass: 'hover:bg-vis-green-70',
      sankeyColour: 'vis-green-50',
      fontClass: 'text-default',
      table: {
        cta: 'components.reporting.automation.fin-insights.jobs.data-gaps.table-cta',
        filters: and(groupings.queryType.personalized, groupings.handled.routedToTeammate),
        chartSeries: [
          metrics.volume,
          metrics.handlingTimeSum,
          metrics.handlingTimeMedian,
          metrics.aiCsatScore,
        ],
        highlightedMetricIndex: 1, // total handling time
      },
      sankey: {
        type: 'queryTypeToRes',
        highlightedLinks: 'personalizedToRoutedToTeam',
      },
      topLine: {
        chartSeries: [metrics.volume],
        filters: and(groupings.queryType.personalized, groupings.handled.routedToTeammate),
        label: intl.t('components.reporting.automation.fin-insights.jobs.data-gaps.top-line.label'),
        description: intl.t(
          'components.reporting.automation.fin-insights.jobs.data-gaps.top-line.description',
        ),
      },
    },
    {
      id: 'identifyFinTasks',
      label: intl.t('components.reporting.automation.fin-insights.jobs.fin-tasks.label'),
      showSuggestions: false,
      backgroundClass: 'bg-vis-green-50',
      hoverBackgroundClass: 'hover:bg-vis-green-70',
      sankeyColour: 'vis-green-50',
      fontClass: 'text-default',
      table: {
        cta: 'components.reporting.automation.fin-insights.jobs.fin-tasks.table-cta',
        filters: and(groupings.queryType.taskBased, groupings.handled.routedToTeammate),
        chartSeries: [
          metrics.volume,
          metrics.handlingTimeSum,
          metrics.handlingTimeMedian,
          metrics.aiCsatScore,
        ],
        highlightedMetricIndex: 1, // total handling time
      },
      sankey: {
        type: 'queryTypeToRes',
        highlightedLinks: 'taskBasedToRoutedToTeam',
      },
      topLine: {
        chartSeries: [metrics.volume],
        filters: and(groupings.queryType.taskBased, groupings.handled.routedToTeammate),
        label: !appService.app.canUseProceduresClosedBeta
          ? intl.t(
              'components.reporting.automation.fin-insights.jobs.fin-tasks.top-line.label-for-tasks',
            )
          : intl.t('components.reporting.automation.fin-insights.jobs.fin-tasks.top-line.label'),
        description: intl.t(
          'components.reporting.automation.fin-insights.jobs.fin-tasks.top-line.description',
        ),
      },
    },
    {
      id: 'reviewLowQualityFinResolutions',
      label: intl.t(
        'components.reporting.automation.fin-insights.jobs.low-quality-fin-resolutions.label',
      ),
      showSuggestions: false,
      backgroundClass: 'bg-vis-violet-50',
      hoverBackgroundClass: 'hover:bg-vis-violet-70',
      sankeyColour: 'vis-violet-50',
      fontClass: 'text-on-neutral',
      table: {
        cta: 'components.reporting.automation.fin-insights.jobs.low-quality-fin-resolutions.table-cta',
        filters: and(groupings.handled.resolvedByFin, groupings.csat.negative),
        chartSeries: [metrics.volume, metrics.negativelyRatedAiCsat],
        highlightedMetricIndex: 1, // negative AI CSAT ratings
      },
      sankey: {
        type: 'resToCSAT',
        highlightedLinks: 'resolvedByFinToNegativeCSAT',
      },
      topLine: {
        chartSeries: [metrics.volume],
        filters: and(groupings.handled.resolvedByFin, groupings.csat.negative),
        label: intl.t(
          'components.reporting.automation.fin-insights.jobs.low-quality-fin-resolutions.top-line.label',
        ),
        description: intl.t(
          'components.reporting.automation.fin-insights.jobs.low-quality-fin-resolutions.top-line.description',
        ),
      },
    },
    {
      id: 'validateFinsResolutions',
      label: intl.t(
        'components.reporting.automation.fin-insights.jobs.validate-fins-resolutions.label',
      ),
      showSuggestions: false,
      backgroundClass: 'bg-vis-violet-50',
      hoverBackgroundClass: 'hover:bg-vis-violet-70',
      sankeyColour: 'vis-violet-50',
      fontClass: 'text-on-neutral',
      table: {
        cta: 'components.reporting.automation.fin-insights.jobs.validate-fins-resolutions.table-cta',
        filters: and(groupings.handled.resolvedByFin, groupings.csat.positive),
        chartSeries: [metrics.volume, metrics.positivelyRatedAiCsat],
        highlightedMetricIndex: 1, // positive AI CSAT ratings
      },
      sankey: {
        type: 'resToCSAT',
        highlightedLinks: 'resolvedByFinToPositiveCSAT',
      },
      topLine: {
        chartSeries: [metrics.volume],
        filters: and(groupings.handled.resolvedByFin, groupings.csat.positive),
        label: intl.t(
          'components.reporting.automation.fin-insights.jobs.validate-fins-resolutions.top-line.label',
        ),
        description: intl.t(
          'components.reporting.automation.fin-insights.jobs.validate-fins-resolutions.top-line.description',
        ),
      },
    },
    {
      id: 'identifyFinInvolvementOpportunities',
      label: intl.t(
        'components.reporting.automation.fin-insights.jobs.involvement-opportunities.label',
      ),
      showSuggestions: false,
      backgroundClass: 'bg-vis-blue-50',
      hoverBackgroundClass: 'hover:bg-vis-blue-70',
      sankeyColour: 'vis-blue-50',
      fontClass: 'text-default',
      table: {
        cta: 'components.reporting.automation.fin-insights.jobs.involvement-opportunities.table-cta',
        filters: and(groupings.queryType.informational, groupings.handled.handledFullyByTeammate),
        chartSeries: [
          metrics.volume,
          metrics.handlingTimeSum,
          metrics.handlingTimeMedian,
          metrics.aiCsatScore,
        ],
        highlightedMetricIndex: 1, // total handling time
      },
      sankey: {
        type: 'queryTypeToRes',
        highlightedLinks: 'infoToTeammate',
      },
      topLine: {
        chartSeries: [metrics.volume],
        filters: and(groupings.queryType.informational, groupings.handled.handledFullyByTeammate),
        label: intl.t(
          'components.reporting.automation.fin-insights.jobs.involvement-opportunities.top-line.label',
        ),
        description: intl.t(
          'components.reporting.automation.fin-insights.jobs.involvement-opportunities.top-line.description',
        ),
      },
    },
  ] as const;
}

type JobId = ReturnType<typeof jobDefinitions>[number]['id'];
type TranslationKey = string;

interface Job {
  id: JobId;
  label: string;
  showSuggestions: boolean;
  backgroundClass: string;
  hoverBackgroundClass: string;
  sankeyColour: string;
  fontClass: string;
  table: {
    cta: TranslationKey;
    filters: LogicalFilter;
    chartSeries: readonly ChartSeriesDefinition[];
    highlightedMetricIndex: number;
  };
  sankey: {
    type: 'resToCSAT' | 'queryTypeToRes';
    highlightedLinks: string;
  };
  topLine: {
    chartSeries: readonly ChartSeriesDefinition[];
    filters: LogicalFilter;
    label: string;
    description: string;
  };
}

interface JobGroupObject {
  id: string;
  label: string;
  chartSeries: ChartSeriesDefinitionWithoutLabel[];
  backgroundClass: string;
  borderClass: string;
  fontClass: string;
}

const jobGroups: Record<string, readonly JobId[]> = {
  improveAnswers: ['identifyFinContentGaps', 'identifyFinDataGaps', 'identifyFinTasks'],
  checkQuality: ['reviewLowQualityFinResolutions', 'validateFinsResolutions'],
  increaseCoverage: ['identifyFinInvolvementOpportunities'],
} as const;

const jobGroupObjects: readonly JobGroupObject[] = [
  {
    label: 'components.reporting.automation.fin-insights.job-groups.improve-answers',
    chartSeries: [{ metric_id: 'fin.resolution_rate' }],
    backgroundClass: 'bg-vis-green-50',
    borderClass: 'border-vis-green-50',
    fontClass: 'text-default',
    id: 'improveAnswers',
  },
  {
    label: 'components.reporting.automation.fin-insights.job-groups.check-quality',
    chartSeries: [
      {
        metric_id:
          'conversation.ai_generated_metrics.csat_score.first_user_conversation_part_created_at',
        filters: {
          type: 'and',
          filters: [
            {
              type: 'category',
              data: {
                property: 'agent_types_involved_grouping',
                attribute: 'conversation.agent_types_involved_grouping',
                values: ['ai-agent'],
              },
            },
          ],
        },
      },
    ],
    backgroundClass: 'bg-vis-violet-50',
    borderClass: 'border-vis-violet-50',
    fontClass: 'text-on-neutral',
    id: 'checkQuality',
  },
];
function jobs(intl: IntlService, appService: AppService) {
  return jobDefinitions(intl, appService) as readonly Job[];
}

export { jobGroups, jobGroupObjects, jobs, type JobId, type Job, type JobGroupObject };
